import { Head, Link } from '@inertiajs/react'
import AdminLayout from '../../../components/layout/AdminLayout'
import StatsCard from '../../../components/dashboard/StatsCard'

export default function AdminDashboardIndex({ stats, recentAgencies, topPerformers, recentActivity }) {
  const revenueGrowth = stats.revenue.last_month > 0
    ? ((stats.revenue.this_month - stats.revenue.last_month) / stats.revenue.last_month * 100).toFixed(1)
    : 0

  return (
    <AdminLayout>
      <Head title="Administration" />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
          <p className="text-gray-600 mt-1">Vue d'ensemble du système</p>
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Agences"
            value={stats.agencies.total}
            subtitle={`${stats.agencies.active} actives`}
            icon="building"
            color="blue"
            href="/admin/agencies"
          />

          <StatsCard
            title="Leads totaux"
            value={stats.leads.total}
            subtitle={`${stats.leads.conversion_rate}% convertis`}
            trend={stats.leads.this_month}
            trendLabel="ce mois"
            icon="users"
            color="green"
            href="/admin/analytics"
          />

          <StatsCard
            title="Matches réseau"
            value={stats.matches.inter_agency}
            subtitle={`${stats.matches.success_rate}% succès`}
            icon="network"
            color="purple"
            href="/admin/analytics"
          />

          <StatsCard
            title="Revenus du mois"
            value={`${(stats.revenue.this_month / 100).toFixed(0)}€`}
            subtitle={`${revenueGrowth > 0 ? '+' : ''}${revenueGrowth}% vs mois dernier`}
            trend={parseFloat(revenueGrowth)}
            icon="currency"
            color="yellow"
            href="/billing"
          />
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top agences performantes */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Top agences performantes</h2>
              <Link href="/admin/agencies" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Voir toutes →
              </Link>
            </div>
            <div className="space-y-3">
              {topPerformers.map((agency, index) => (
                <div key={agency.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{agency.name}</h3>
                      <p className="text-sm text-gray-600">{agency.city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{agency.matches_count} matches</div>
                    <div className="text-sm text-gray-600">{agency.success_rate}% succès</div>
                  </div>
                </div>
              ))}
              {topPerformers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucune agence performante pour le moment
                </div>
              )}
            </div>
          </div>

          {/* Statistiques collaboration */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Collaboration</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Agences actives</div>
                <div className="text-2xl font-bold text-gray-900">{stats.agencies.active}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Collaboration activée</div>
                <div className="text-2xl font-bold text-gray-900">{stats.agencies.collaboration_enabled}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Matches inter-agences</div>
                <div className="text-2xl font-bold text-gray-900">{stats.matches.inter_agency}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Taux de succès</div>
                <div className="text-2xl font-bold text-green-600">{stats.matches.success_rate}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Activité récente */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Activité récente</h2>
          <div className="space-y-2">
            {recentActivity.slice(0, 10).map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'lead_created' ? 'bg-blue-500' : 'bg-purple-500'
                  }`} />
                  <div>
                    <p className="text-sm text-gray-900">{activity.description}</p>
                    {activity.agency && (
                      <p className="text-xs text-gray-500">{activity.agency}</p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(activity.timestamp).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Agences récentes */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Agences récentes</h2>
            <Link href="/admin/agencies" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              Voir toutes →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentAgencies.map((agency) => (
              <Link
                key={agency.id}
                href={`/admin/agencies/${agency.id}`}
                className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{agency.name}</h3>
                  {agency.active ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">{agency.city}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>{agency.leads_count} leads</span>
                  <span>•</span>
                  <span>{agency.users_count} users</span>
                  <span>•</span>
                  <span>{agency.credits} crédits</span>
                </div>
                {agency.collaboration_enabled && (
                  <div className="mt-2">
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
                      Collaboration
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
